// src/components/inventory/InventoryEditPanel.js
// DROP-IN REPLACEMENT - WITH PRODUCT NAME PARSER
//
// ✅ Fixed: Input fields no longer lose focus on typing
// ✅ FieldRow moved OUTSIDE component so it doesn't remount inputs each render
// ✅ Uses useRef for stable form references
// ✅ Improved performance with useCallback
// ✅ Labels on the LEFT of the input on the SAME LINE (compact rows)
// ✅ ADDED: Product Name Parser integration

import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  Timestamp,
  doc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  getDoc,
  deleteField,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Eye, Shield, Users, X, EyeOff, Lock, Package, Wand2 } from "lucide-react";

// Import the ProductParsingPanel
import ProductParsingPanel from "./ProductParsingPanel";
import AttachmentsPanel from "../attachments/AttachmentsPanel";
import {
  buildInventoryPriceChangeDetail,
  createInventoryPriceChangeWrite,
} from "../../utils/inventoryPriceChanges";

// helpers
const safeArray = (v) => (Array.isArray(v) ? v : []);
const uniqStrings = (arr) =>
  Array.from(new Set(safeArray(arr).filter(Boolean).map(String)));
const asBool = (v) => v === true || v === "true" || v === 1 || v === "1";
const normalizeSku = (value) => String(value || "").trim().toLowerCase();

const normalizeAuditValue = (value) => {
  if (value === undefined || value === null) return null;
  if (Array.isArray(value)) return value.map(normalizeAuditValue);
  if (typeof value === "object") {
    if (typeof value.toDate === "function") return value.toDate().toISOString();
    if ("seconds" in value && typeof value.seconds === "number") {
      return new Date(value.seconds * 1000).toISOString();
    }
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, nested]) => nested !== undefined)
        .map(([key, nested]) => [key, normalizeAuditValue(nested)])
    );
  }
  return value;
};

const valuesEqualForAudit = (a, b) =>
  JSON.stringify(normalizeAuditValue(a)) === JSON.stringify(normalizeAuditValue(b));

// ✅ IMPORTANT: MUST be outside InventoryEditPanel (stable component identity)
const FieldRow = React.memo(function FieldRow({
  label,
  required,
  children,
  className = "",
}) {
  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-[140px,1fr] items-center gap-3 min-w-0 ${className}`}
    >
      <label className="text-sm font-medium text-slate-700 truncate">
        {label}
        {required ? <span className="text-red-500 ml-1">*</span> : null}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  );
});
FieldRow.displayName = "FieldRow";

const InventoryEditPanel = ({ isOpen, onClose, companyId, item, onUpdated }) => {
  const auth = getAuth();
  const db = getFirestore();
  const storage = getStorage();

  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const [myProfile, setMyProfile] = useState(null);
  const myRole = useMemo(
    () => (myProfile?.role || "").toLowerCase(),
    [myProfile]
  );
  const isAdminLevel = useMemo(
    () => myRole === "admin" || myRole === "owner",
    [myRole]
  );

  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");

  // Product parsing panel state
  const [showParser, setShowParser] = useState(false);

  // Use refs to store form data to prevent re-renders on every keystroke
  const formRef = useRef({
    sku: "",
    name: "",
    category: "",
    subCategory: "",
    brand: "",
    packSize: "",
    price: "",
    stock: "",
    lowStockThreshold: "10",
    sampleable: true,
    featured: false,
    seasonal: false,
    contractItem: false,
    hiddenFromCustomer: false,
    description: "",
    customerFacingDescription: "",
    internalNotes: "",
    countryOfOrigin: "",
    rebateDetails: "",
    volumeDiscountDetails: "",
    contractDetails: "",
    imageUrl: "",
    groupIds: [],
  });

  // Local state for rendering - only update when needed
  const [formState, setFormState] = useState(formRef.current);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const customValuesRef = useRef({});
  const [customValuesState, setCustomValuesState] = useState({});
  const [historyRows, setHistoryRows] = useState([]);
  const [userLabels, setUserLabels] = useState({});
  const itemId = item?.id || "";
  const itemCreatedBy = item?.createdBy || "";
  const itemUpdatedBy = item?.updatedBy || "";

  // Create a stable reference for filtered groups
  const filteredGroups = useMemo(() => {
    const q = (groupSearch || "").toLowerCase().trim();
    if (!q) return groups;
    return groups.filter((g) => (g?.name || "").toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const selectedGroupIds = useMemo(() => {
    return uniqStrings(formState.groupIds);
  }, [formState.groupIds]);

  const canEdit = isAdminLevel;
  const groupsDisabled = !canEdit;
  const showPricingFlag = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    const v = flags.showInventoryPricingToEndUsers ?? cs.showInventoryPricingToEndUsers;
    return asBool(v);
  }, [companySettings]);
  const onlyOwnerCanSeePricing = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    return asBool(flags.onlyOwnerCanSeeInventoryPricing);
  }, [companySettings]);
  const canSeePricing = useMemo(() => {
    if (onlyOwnerCanSeePricing) return myRole === "owner";
    return isAdminLevel || showPricingFlag;
  }, [isAdminLevel, myRole, onlyOwnerCanSeePricing, showPricingFlag]);
  const commercialTermsVisibility = useMemo(() => {
    const cs = companySettings || {};
    const flags = cs.featureFlags || {};
    const value = String(flags.commercialTermsVisibility || "none").trim().toLowerCase();
    return ["none", "admin", "all"].includes(value) ? value : "none";
  }, [companySettings]);
  const canSeeCommercialTerms = useMemo(() => {
    if (myRole === "owner") return true;
    if (commercialTermsVisibility === "all") return true;
    if (commercialTermsVisibility === "admin") return myRole === "admin" || myRole === "super_admin";
    return false;
  }, [commercialTermsVisibility, myRole]);

  const formatAuditDate = useCallback((value) => {
    if (!value) return "—";
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : value instanceof Date
        ? value
        : new Date(value);

    if (Number.isNaN(date?.getTime?.())) return "—";
    return date.toLocaleString();
  }, []);

  const labelForUserId = useCallback(
    (value) => {
      const key = String(value || "").trim();
      if (!key) return "—";
      return userLabels[key] || (key === auth.currentUser?.uid ? "You" : key);
    },
    [auth.currentUser?.uid, userLabels]
  );

  // Stable event handlers
  const toggleGroup = useCallback(
    (gid) => {
      if (groupsDisabled) return;
      const id = String(gid);
      const set = new Set(uniqStrings(formRef.current.groupIds));
      if (set.has(id)) set.delete(id);
      else set.add(id);

      const newGroupIds = Array.from(set);
      formRef.current.groupIds = newGroupIds;
      setFormState((prev) => ({ ...prev, groupIds: newGroupIds }));
    },
    [groupsDisabled]
  );

  const handleChange = useCallback((field, value) => {
    formRef.current[field] = value;
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCustomChange = useCallback((key, value) => {
    customValuesRef.current[key] = value;
    setCustomValuesState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClearGroups = useCallback(() => {
    if (groupsDisabled) return;
    formRef.current.groupIds = [];
    setFormState((prev) => ({ ...prev, groupIds: [] }));
    setGroupSearch("");
  }, [groupsDisabled]);

  // Handle parser fixes
  const handleParserApply = useCallback((fixedProduct) => {
    // Update all form fields with the fixed product data
    Object.keys(fixedProduct).forEach(key => {
      if (key in formRef.current) {
        formRef.current[key] = fixedProduct[key];
      }
    });
    
    // Update state for re-render
    setFormState(prev => ({ ...prev, ...fixedProduct }));
    
    // Close parser
    setShowParser(false);
  }, []);

  // Load initial data
  useEffect(() => {
    const load = async () => {
      if (!companyId || !isOpen) return;

      try {
        const uid = auth.currentUser?.uid;
        if (uid) {
          const profileSnap = await getDoc(doc(db, "users", uid));
          if (profileSnap.exists()) {
            setMyProfile({ id: profileSnap.id, ...profileSnap.data() });
          } else {
            setMyProfile(null);
          }
        }
      } catch (err) {
        console.warn("[InventoryEditPanel] Could not load my profile:", err);
        setMyProfile(null);
      }

      try {
        const companySnap = await getDoc(doc(db, "companies", companyId));
        if (companySnap.exists()) {
          setCompanySettings(companySnap.data() || {});
        } else {
          setCompanySettings({});
        }
      } catch (err) {
        console.warn("[InventoryEditPanel] Could not load company settings:", err);
        setCompanySettings({});
      }

      try {
        const groupsSnap = await getDocs(
          collection(db, "companies", companyId, "groups")
        );
        const list = groupsSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const sysA = a.isSystem ? 0 : 1;
            const sysB = b.isSystem ? 0 : 1;
            if (sysA !== sysB) return sysA - sysB;
            return (a.name || "")
              .toLowerCase()
              .localeCompare((b.name || "").toLowerCase());
          });
        setGroups(list);
      } catch (err) {
        console.error("[InventoryEditPanel] Error loading groups:", err);
        setGroups([]);
      }

      try {
        const colRef = collection(
          db,
          "companies",
          companyId,
          "inventoryFieldDefinitions"
        );
        const snap = await getDocs(colRef);
        const defs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        defs.sort((a, b) => {
          const orderA = typeof a.order === "number" ? a.order : 9999;
          const orderB = typeof b.order === "number" ? b.order : 9999;
          if (orderA !== orderB) return orderA - orderB;
          return (a.label || "")
            .toLowerCase()
            .localeCompare((b.label || "").toLowerCase());
        });

        setFieldDefinitions(defs);
      } catch (err) {
        console.error(
          "Error loading inventory field definitions in edit panel:",
          err
        );
      }

      try {
        if (itemId) {
          const historySnap = await getDocs(
            collection(db, "companies", companyId, "inventory", itemId, "changelog")
          );
          const rows = historySnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const aMs =
                typeof a?.changedAt?.toDate === "function"
                  ? a.changedAt.toDate().getTime()
                  : typeof a?.changedAt?.seconds === "number"
                  ? a.changedAt.seconds * 1000
                  : 0;
              const bMs =
                typeof b?.changedAt?.toDate === "function"
                  ? b.changedAt.toDate().getTime()
                  : typeof b?.changedAt?.seconds === "number"
                  ? b.changedAt.seconds * 1000
                  : 0;
              return bMs - aMs;
            })
            .slice(0, 8);

          setHistoryRows(rows);

          const idsToLoad = Array.from(
            new Set(
              [itemUpdatedBy, itemCreatedBy, ...rows.map((row) => row?.changedBy)]
                .map((value) => String(value || "").trim())
                .filter(Boolean)
            )
          );

          if (idsToLoad.length) {
            const loaded = {};
            await Promise.all(
              idsToLoad.map(async (uid) => {
                try {
                  const userSnap = await getDoc(doc(db, "users", uid));
                  if (!userSnap.exists()) return;
                  const userData = userSnap.data() || {};
                  loaded[uid] =
                    String(userData?.fullName || "").trim() ||
                    String(userData?.email || "").trim() ||
                    uid;
                } catch {
                  // Keep raw uid fallback if blocked.
                }
              })
            );
            setUserLabels(loaded);
          } else {
            setUserLabels({});
          }
        } else {
          setHistoryRows([]);
          setUserLabels({});
        }
      } catch (err) {
        console.warn("[InventoryEditPanel] Could not load changelog:", err);
        setHistoryRows([]);
        setUserLabels({});
      }
    };

    load();
  }, [companyId, db, isOpen, auth, itemId, itemCreatedBy, itemUpdatedBy]);

  // Reset form when item changes
  useEffect(() => {
    if (!item) return;

    const derivedHiddenFromCustomer =
      item.hiddenFromCustomer === true
        ? true
        : item.customerVisible === false
        ? true
        : false;

    // Support legacy unsampleable by deriving sampleable if needed
    const initialSampleable =
      typeof item.sampleable === "boolean"
        ? item.sampleable
        : item.unsampleable === true
        ? false
        : true;

    const initialGroupIds = uniqStrings(item.groupIds);

    const newForm = {
      sku: item.sku || "",
      name: item.name || "",
      category: item.category || "",
      subCategory: item.subCategory || "",
      brand: item.brand || "",
      packSize: item.packSize || "",
      price: item.price ?? "",
      stock: item.stock ?? "",
      lowStockThreshold: item.lowStockThreshold ?? "10",
      sampleable: initialSampleable,
      featured: item.featured ?? false,
      seasonal: item.seasonal ?? false,
      contractItem: item.contractItem ?? false,
      hiddenFromCustomer: derivedHiddenFromCustomer,
      description: item.description || "",
      customerFacingDescription: item.customerFacingDescription || "",
      internalNotes: item.internalNotes || "",
      countryOfOrigin: item.countryOfOrigin || "",
      rebateDetails: item.rebateDetails || "",
      volumeDiscountDetails: item.volumeDiscountDetails || "",
      contractDetails: item.contractDetails || "",
      imageUrl: item.imageUrl || "",
      groupIds: initialGroupIds,
    };

    formRef.current = newForm;
    setFormState(newForm);

    customValuesRef.current = item.custom || {};
    setCustomValuesState(item.custom || {});

    setUploadError("");
    setUploadingImage(false);
    setGroupSearch("");
    setShowParser(false); // Close parser if open
  }, [item]);

  const handleImageFileChange = async (file) => {
    if (!canEdit) return;
    if (!file || !companyId || !item?.id) return;

    setUploadError("");
    setUploadingImage(true);

    try {
      const imageRef = ref(
        storage,
        `companies/${companyId}/inventory/${item.id}/main_${Date.now()}_${file.name}`
      );

      await uploadBytes(imageRef, file);
      const downloadURL = await getDownloadURL(imageRef);

      formRef.current.imageUrl = downloadURL;
      setFormState((prev) => ({ ...prev, imageUrl: downloadURL }));
    } catch (err) {
      console.error("Error uploading product image:", err);
      setUploadError("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!companyId || !item?.id || !formRef.current.sku.trim() || !formRef.current.name.trim()) return;
    if (uploadingImage) return;
    if (!canEdit) return;

    if (!formRef.current.name.trim()) {
      alert("Product name is required.");
      return;
    }

    if (!formRef.current.sku.trim()) {
      alert("SKU is required.");
      return;
    }

    setSaving(true);
    try {
      const uid = auth.currentUser?.uid || null;
      const form = formRef.current;

      const updatedData = {
        sku: form.sku.trim() || null,
        name: form.name.trim(),
        category: form.category.trim() || null,
        subCategory: form.subCategory.trim() || null,
        brand: form.brand.trim() || null,
        packSize: form.packSize.trim() || null,
        price: form.price !== "" ? Number(form.price) : null,
        stock: form.stock !== "" ? Number(form.stock) : 0,
        lowStockThreshold:
          form.lowStockThreshold !== ""
            ? Number(form.lowStockThreshold)
            : 10,
        description: form.description.trim() || null,
        customerFacingDescription: form.customerFacingDescription.trim() || null,
        internalNotes: form.internalNotes.trim() || null,
        countryOfOrigin: form.countryOfOrigin.trim() || null,
        rebateDetails: form.rebateDetails.trim() || null,
        volumeDiscountDetails: form.volumeDiscountDetails.trim() || null,
        contractDetails: form.contractDetails.trim() || null,
        imageUrl: form.imageUrl || null,

        // ✅ NEW MODEL: groupIds ONLY
        groupIds: uniqStrings(form.groupIds),

        // ✅ Cleanup legacy fields if they exist
        hiddenFromEndUsers: deleteField(),
        visibility: deleteField(),

        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };

      // Admin-controlled toggles
      const sampleable = Boolean(form.sampleable);
      Object.assign(updatedData, {
        sampleable,
        // keep legacy field in sync (no UI for it)
        unsampleable: !sampleable,

        featured: Boolean(form.featured),
        seasonal: Boolean(form.seasonal),
        contractItem: Boolean(form.contractItem),
        hiddenFromCustomer: Boolean(form.hiddenFromCustomer),
        customerVisible: !Boolean(form.hiddenFromCustomer),
      });

      // Custom fields
      const customData = { ...(item.custom || {}) };

      fieldDefinitions.forEach((def) => {
        const key = def.key || def.id;
        if (!key) return;

        const rawValue = customValuesRef.current[key];
        const fieldType = String(def.fieldType || def.type || "text").toLowerCase();

        if (rawValue === undefined || rawValue === null || rawValue === "") {
          customData[key] = null;
          return;
        }

        if (fieldType === "number") {
          const num = Number(rawValue);
          customData[key] = isNaN(num) ? null : num;
        } else if (fieldType === "boolean") {
          customData[key] = !!rawValue;
        } else {
          customData[key] = rawValue;
        }
      });

      if (
        fieldDefinitions.length > 0 ||
        (item.custom && Object.keys(item.custom).length > 0)
      ) {
        updatedData.custom = customData;
      }

      const previousSku = String(item?.sku || "").trim();
      const nextSku = String(updatedData.sku || "").trim();
      const previousSkuNorm = normalizeSku(previousSku);
      const nextSkuNorm = normalizeSku(nextSku);

      if (nextSkuNorm && nextSkuNorm !== previousSkuNorm) {
        const inventorySnap = await getDocs(collection(db, "companies", companyId, "inventory"));
        const conflictingDoc = inventorySnap.docs.find((docSnap) => {
          if (docSnap.id === item.id) return false;
          const data = docSnap.data() || {};
          const currentSkuNorm = normalizeSku(data?.sku);
          const historySkuNorms = safeArray(data?.previousSkus).map(normalizeSku).filter(Boolean);
          return currentSkuNorm === nextSkuNorm || historySkuNorms.includes(nextSkuNorm);
        });

        if (conflictingDoc) {
          alert("That SKU already exists on another product or in another product's SKU history.");
          setSaving(false);
          return;
        }
      }

      if (previousSkuNorm && nextSkuNorm !== previousSkuNorm) {
        updatedData.previousSkus = uniqStrings([...(safeArray(item?.previousSkus) || []), previousSku]);
      }

      const itemRef = doc(db, "companies", companyId, "inventory", item.id);
      await updateDoc(itemRef, updatedData);

      const previousComparable = {
        sku: item?.sku ?? null,
        name: item?.name ?? null,
        category: item?.category ?? null,
        subCategory: item?.subCategory ?? null,
        brand: item?.brand ?? null,
        packSize: item?.packSize ?? null,
        price: item?.price ?? null,
        stock: item?.stock ?? 0,
        lowStockThreshold: item?.lowStockThreshold ?? 10,
        description: item?.description ?? null,
        customerFacingDescription: item?.customerFacingDescription ?? null,
        internalNotes: item?.internalNotes ?? null,
        countryOfOrigin: item?.countryOfOrigin ?? null,
        rebateDetails: item?.rebateDetails ?? null,
        volumeDiscountDetails: item?.volumeDiscountDetails ?? null,
        contractDetails: item?.contractDetails ?? null,
        imageUrl: item?.imageUrl ?? null,
        previousSkus: uniqStrings(item?.previousSkus),
        groupIds: uniqStrings(item?.groupIds),
        sampleable:
          typeof item?.sampleable === "boolean"
            ? item.sampleable
            : item?.unsampleable === true
            ? false
            : true,
        unsampleable: item?.unsampleable ?? false,
        featured: item?.featured ?? false,
        seasonal: item?.seasonal ?? false,
        contractItem: item?.contractItem ?? false,
        hiddenFromCustomer:
          item?.hiddenFromCustomer === true ? true : item?.customerVisible === false ? true : false,
        customerVisible: item?.customerVisible === true,
        custom: item?.custom || {},
      };

      const nextComparable = Object.fromEntries(
        Object.entries(updatedData).filter(([key]) =>
          !["updatedAt", "updatedBy", "hiddenFromEndUsers", "visibility"].includes(key)
        )
      );

      const changedFields = Object.keys(nextComparable).filter(
        (key) => !valuesEqualForAudit(previousComparable[key], nextComparable[key])
      );

      console.log("[InventoryEditPanel] save changedFields", {
        inventoryId: item.id,
        name: updatedData.name || item?.name || "",
        previousPrice: previousComparable.price ?? null,
        nextPrice: nextComparable.price ?? null,
        changedFields,
      });

      const changes = changedFields.reduce((acc, key) => {
        acc[key] = {
          from: normalizeAuditValue(previousComparable[key]),
          to: normalizeAuditValue(nextComparable[key]),
        };
        return acc;
      }, {});

      const priceChangeDetail = changedFields.includes("price")
        ? buildInventoryPriceChangeDetail({
            companyId,
            inventoryId: item.id,
            name: updatedData.name || item?.name || "",
            sku: updatedData.sku || item?.sku || "",
            category: updatedData.category || item?.category || "",
            changedBy: uid,
            changedByLabel: auth.currentUser?.displayName || null,
            source: "editPanel",
            type: "updatedViaPanel",
            from: previousComparable.price ?? null,
            to: nextComparable.price ?? null,
          })
        : null;

      console.log("[InventoryEditPanel] computed priceChangeDetail", priceChangeDetail);

      if (priceChangeDetail) {
        try {
          const { ref: priceChangeRef, payload: priceChangePayload } = createInventoryPriceChangeWrite({
            db,
            companyId,
            detail: priceChangeDetail,
          });
          await setDoc(priceChangeRef, priceChangePayload);
          console.log("[InventoryEditPanel] inventoryPriceChanges write success", {
            inventoryId: item.id,
            companyId,
            path: `companies/${companyId}/inventoryPriceChanges/${priceChangeRef.id}`,
            priceChange: priceChangeDetail,
          });
        } catch (priceFeedErr) {
          console.warn("[InventoryEditPanel] inventoryPriceChanges write blocked:", priceFeedErr);
        }
      }

      // optional changelog (ignore if blocked by rules)
      try {
        const changelogRef = doc(
          collection(db, "companies", companyId, "inventory", item.id, "changelog"),
          `${item.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
        );
        await setDoc(changelogRef, {
          changedAt: Timestamp.now(),
          changedBy: uid,
          changedByLabel: auth.currentUser?.displayName || null,
          type: "updatedViaPanel",
          changedFields,
          changes,
          priceChange: priceChangeDetail
            ? {
                from: priceChangeDetail.from,
                to: priceChangeDetail.to,
              }
            : null,
          source: "editPanel",
        });
        console.log("[InventoryEditPanel] changelog write success", {
          inventoryId: item.id,
          companyId,
          path: `companies/${companyId}/inventory/${item.id}/changelog/${changelogRef.id}`,
          priceChange: priceChangeDetail,
        });
      } catch (logErr) {
        console.warn("[InventoryEditPanel] changelog write blocked:", logErr);
      }

      if (priceChangeDetail && typeof window !== "undefined") {
        console.log("[InventoryEditPanel] dispatching inventory-price-change event", priceChangeDetail);
        window.dispatchEvent(
          new CustomEvent("inventory-price-change", {
            detail: priceChangeDetail,
          })
        );
      }

      onUpdated?.();
      onClose?.();
    } catch (err) {
      console.error("Error updating product:", err);
      alert("Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !item) return null;

  const noGroupsSelected = selectedGroupIds.length === 0;
  const currentUpdatedAt = item?.updatedAt || item?.createdAt || null;

  const inputBase =
    "w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";
  const inputDisabled = "bg-slate-100 text-slate-500 cursor-not-allowed";

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-hidden p-0 sm:items-center sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative flex w-full max-w-6xl max-h-[94vh] flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 truncate">
              Edit Product
            </h2>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              Update product details, visibility settings, and group access
            </p>
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 sm:p-6">
          {!canEdit && (
            <div className="mb-4 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center gap-2">
              <Lock className="w-4 h-4" />
              You can view this product, but only Admin/Owner can edit inventory.
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3 lg:gap-6">
            {/* Left Column - Basic Info */}
            <div className="lg:col-span-2 space-y-3 sm:space-y-4 lg:space-y-5 min-w-0">
              {/* Core Fields */}
              <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Product Information
                  </h3>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setShowParser(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-purple-300 text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                      <Wand2 className="w-3.5 h-3.5" />
                      Analyze & Parse Name
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-w-0">
                  <div className="md:col-span-2 min-w-0">
                    <FieldRow label="Product Name" required>
                      <input
                        type="text"
                        required
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.name}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="SKU" required className="sm:grid-cols-1 gap-1 items-start">
                      <input
                        type="text"
                        required
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.sku}
                        onChange={(e) => handleChange("sku", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Category" className="sm:grid-cols-1 gap-1 items-start">
                      <input
                        type="text"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.category}
                        onChange={(e) => handleChange("category", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Subcategory" className="sm:grid-cols-1 gap-1 items-start">
                      <input
                        type="text"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.subCategory}
                        onChange={(e) => handleChange("subCategory", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Brand" className="sm:grid-cols-1 gap-1 items-start">
                      <input
                        type="text"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.brand}
                        onChange={(e) => handleChange("brand", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Pack Size" className="grid-cols-[108px_minmax(0,1fr)] gap-2">
                      <input
                        type="text"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.packSize}
                        onChange={(e) => handleChange("packSize", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Origin" className="grid-cols-[108px_minmax(0,1fr)] gap-2">
                      <input
                        type="text"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.countryOfOrigin}
                        onChange={(e) =>
                          handleChange("countryOfOrigin", e.target.value)
                        }
                      />
                    </FieldRow>
                  </div>

                  {canSeePricing ? (
                    <div className="min-w-0">
                      <FieldRow label="Price" className="grid-cols-[108px_minmax(0,1fr)] gap-2">
                        <input
                          type="number"
                          step="0.01"
                          disabled={!canEdit}
                          className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                          value={formState.price}
                          onChange={(e) => handleChange("price", e.target.value)}
                        />
                      </FieldRow>
                    </div>
                  ) : null}

                  <div className="min-w-0">
                    <FieldRow label="Stock" className="grid-cols-[108px_minmax(0,1fr)] gap-2">
                      <input
                        type="number"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.stock}
                        onChange={(e) => handleChange("stock", e.target.value)}
                      />
                    </FieldRow>
                  </div>

                  <div className="min-w-0">
                    <FieldRow label="Low Stock" className="grid-cols-[108px_minmax(0,1fr)] gap-2">
                      <input
                        type="number"
                        disabled={!canEdit}
                        className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                        value={formState.lowStockThreshold}
                        onChange={(e) =>
                          handleChange("lowStockThreshold", e.target.value)
                        }
                      />
                    </FieldRow>
                  </div>

                  <div className="md:col-span-2 min-w-0">
                    <FieldRow label="Internal Description">
                      <textarea
                        disabled={!canEdit}
                        className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          !canEdit ? inputDisabled : ""
                        }`}
                        rows={3}
                        value={formState.description}
                        onChange={(e) =>
                          handleChange("description", e.target.value)
                        }
                      />
                    </FieldRow>
                  </div>

                  <div className="md:col-span-2 min-w-0">
                    <FieldRow label="Customer Description">
                      <textarea
                        disabled={!canEdit}
                        className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          !canEdit ? inputDisabled : ""
                        }`}
                        rows={2}
                        value={formState.customerFacingDescription}
                        onChange={(e) =>
                          handleChange("customerFacingDescription", e.target.value)
                        }
                      />
                    </FieldRow>
                  </div>

                  <div className="md:col-span-2 min-w-0">
                    <FieldRow label="Internal Notes">
                      <textarea
                        disabled={!canEdit}
                        className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          !canEdit ? inputDisabled : ""
                        }`}
                        rows={2}
                        value={formState.internalNotes}
                        onChange={(e) =>
                          handleChange("internalNotes", e.target.value)
                        }
                      />
                    </FieldRow>
                  </div>

                  {canSeeCommercialTerms ? (
                    <>
                      <div className="md:col-span-2 min-w-0">
                        <FieldRow label="Rebates">
                          <textarea
                            disabled={!canEdit}
                            className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                              !canEdit ? inputDisabled : ""
                            }`}
                            rows={2}
                            value={formState.rebateDetails}
                            onChange={(e) => handleChange("rebateDetails", e.target.value)}
                          />
                        </FieldRow>
                      </div>

                      <div className="md:col-span-2 min-w-0">
                        <FieldRow label="Volume Discounts">
                          <textarea
                            disabled={!canEdit}
                            className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                              !canEdit ? inputDisabled : ""
                            }`}
                            rows={2}
                            value={formState.volumeDiscountDetails}
                            onChange={(e) => handleChange("volumeDiscountDetails", e.target.value)}
                          />
                        </FieldRow>
                      </div>

                      <div className="md:col-span-2 min-w-0">
                        <FieldRow label="Contracts">
                          <textarea
                            disabled={!canEdit}
                            className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                              !canEdit ? inputDisabled : ""
                            }`}
                            rows={2}
                            value={formState.contractDetails}
                            onChange={(e) => handleChange("contractDetails", e.target.value)}
                          />
                        </FieldRow>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Visibility Settings */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-emerald-700" />
                    <h3 className="text-sm font-semibold text-slate-900">
                      Visibility & Sampling
                    </h3>
                  </div>
                  {!canEdit && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                      <Lock className="w-3 h-3" /> Admin Only
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3 sm:gap-2">
                  <label
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors ${
                      !canEdit ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Package className="w-4 h-4 text-blue-700 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        Sampleable
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formState.sampleable}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleChange("sampleable", e.target.checked)
                      }
                    />
                  </label>

                  <label
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors ${
                      !canEdit ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Shield className="w-4 h-4 text-amber-700 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        Featured
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formState.featured}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleChange("featured", e.target.checked)
                      }
                    />
                  </label>

                  <label
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors ${
                      !canEdit ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <EyeOff className="w-4 h-4 text-slate-600 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        Hide Customers
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formState.hiddenFromCustomer}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleChange("hiddenFromCustomer", e.target.checked)
                      }
                    />
                  </label>

                </div>

                <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-2">
                  <label
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors ${
                      !canEdit ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Shield className="w-4 h-4 text-sky-700 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        Seasonal
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formState.seasonal}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleChange("seasonal", e.target.checked)
                      }
                    />
                  </label>

                  <label
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:bg-slate-50 transition-colors ${
                      !canEdit ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Shield className="w-4 h-4 text-violet-700 shrink-0" />
                      <span className="text-sm font-medium text-slate-900 truncate">
                        Contract Item
                      </span>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={!!formState.contractItem}
                      disabled={!canEdit}
                      onChange={(e) =>
                        handleChange("contractItem", e.target.checked)
                      }
                    />
                  </label>
                </div>
              </div>

              {/* Custom Fields */}
              {fieldDefinitions.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">
                    Custom Fields
                  </h3>

                <div className="space-y-2.5 sm:space-y-3">
                    {fieldDefinitions.map((def) => {
                      const key = def.key || def.id;
                      if (!key) return null;

                      const label = def.label || key;
                      const required = !!def.required;
                      const fieldType = String(
                        def.fieldType || def.type || "text"
                      ).toLowerCase();
                      const value = customValuesState[key];

                      if (fieldType === "boolean") {
                        return (
                          <div key={key} className={`${!canEdit ? "opacity-60" : ""}`}>
                            <FieldRow label={label} required={required}>
                              <div className="flex items-center justify-end">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4"
                                  checked={!!value}
                                  disabled={!canEdit}
                                  onChange={(e) =>
                                    handleCustomChange(key, e.target.checked)
                                  }
                                />
                              </div>
                            </FieldRow>
                          </div>
                        );
                      }

                      if (fieldType === "select") {
                        const options = Array.isArray(def.options) ? def.options : [];
                        return (
                          <div key={key} className={`min-w-0 ${!canEdit ? "opacity-60" : ""}`}>
                            <FieldRow label={label} required={required}>
                              <select
                                disabled={!canEdit}
                                className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white ${
                                  !canEdit ? inputDisabled : ""
                                }`}
                                value={value ?? ""}
                                onChange={(e) =>
                                  handleCustomChange(key, e.target.value)
                                }
                              >
                                <option value="">Select…</option>
                                {options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </FieldRow>
                          </div>
                        );
                      }

                      if (fieldType === "number") {
                        return (
                          <div key={key} className={`min-w-0 ${!canEdit ? "opacity-60" : ""}`}>
                            <FieldRow label={label} required={required}>
                              <input
                                type="number"
                                disabled={!canEdit}
                                className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                                value={value ?? ""}
                                onChange={(e) =>
                                  handleCustomChange(key, e.target.value)
                                }
                              />
                            </FieldRow>
                          </div>
                        );
                      }

                      return (
                        <div key={key} className={`min-w-0 ${!canEdit ? "opacity-60" : ""}`}>
                          <FieldRow label={label} required={required}>
                            <input
                              type="text"
                              disabled={!canEdit}
                              className={`${inputBase} ${!canEdit ? inputDisabled : ""}`}
                              value={value ?? ""}
                              onChange={(e) => handleCustomChange(key, e.target.value)}
                            />
                          </FieldRow>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Image + Groups */}
            <div className="space-y-5 min-w-0">
              {/* Image */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                  Product Image
                </h3>

                {formState.imageUrl && (
                  <div className="mb-3 border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                    <img
                      src={formState.imageUrl}
                      alt={formState.name || "Product"}
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                )}

                <input
                  type="file"
                  accept="image/*"
                  disabled={!canEdit}
                  className={`block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer ${
                    !canEdit ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  onChange={(e) =>
                    handleImageFileChange(e.target.files && e.target.files[0])
                  }
                />

                {uploadError && (
                  <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-700" />
                  <div className="text-sm font-semibold text-slate-900">Sales Flyers & Files</div>
                </div>

                <AttachmentsPanel
                  companyId={companyId}
                  parentType="inventory"
                  parentId={item.id}
                  currentUserId={auth.currentUser?.uid || null}
                  readOnly={!canEdit}
                  title="Inventory Attachments"
                  parentTitle={(formState.name || item?.name || formState.sku || item?.sku || "").trim() || null}
                  parentStatus={(formState.category || item?.category || "").trim() || null}
                  pageSize={50}
                />
              </div>

              {/* Groups (End-user visibility via groupIds) */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Users className="w-4 h-4 text-blue-700 shrink-0" />
                    <h3 className="text-sm font-semibold text-slate-900 truncate">
                      Visibility Groups
                    </h3>
                  </div>
                  {!canEdit && <Lock className="w-3.5 h-3.5 text-amber-600" />}
                </div>

                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="text-xs text-slate-600 min-w-0">
                    End users see this item only if their groups intersect{" "}
                    <span className="font-semibold">groupIds</span>.
                  </div>
                  <button
                    type="button"
                    onClick={handleClearGroups}
                    disabled={groupsDisabled || selectedGroupIds.length === 0}
                    className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Clear all groups (hides from end users)"
                  >
                    Clear groups
                  </button>
                </div>

                {noGroupsSelected ? (
                  <div className="mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    ⚠️ No groups selected — end users won't see this item (Admin/Owner only)
                  </div>
                ) : null}

                <input
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  placeholder="Search groups..."
                  className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3 ${
                    groupsDisabled ? "bg-slate-100 cursor-not-allowed" : ""
                  }`}
                  disabled={groupsDisabled}
                />

                <div
                  className={`border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto ${
                    groupsDisabled ? "opacity-60" : ""
                  }`}
                >
                  {filteredGroups.length === 0 ? (
                    <div className="p-3 text-sm text-slate-500 text-center">
                      No groups found
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {filteredGroups.map((g) => {
                        const checked = selectedGroupIds.includes(String(g.id));
                        const isAllStaff =
                          g.id === "grp_all_staff" ||
                          (g.name || "").toLowerCase() === "all staff";

                        return (
                          <li key={g.id} className="px-3 py-2 hover:bg-slate-50">
                            <label
                              className={`flex items-center gap-2 text-sm min-w-0 ${
                                !groupsDisabled
                                  ? "cursor-pointer"
                                  : "cursor-not-allowed"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="w-4 h-4 shrink-0"
                                checked={checked}
                                disabled={groupsDisabled}
                                onChange={() => toggleGroup(g.id)}
                              />
                              <span className="flex-1 min-w-0 truncate">
                                {g.name || g.id}
                                {isAllStaff && (
                                  <span className="ml-1 text-xs text-blue-600">
                                    (All Staff)
                                  </span>
                                )}
                              </span>
                              {g.isSystem && (
                                <span className="shrink-0 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                  System
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  Selected:{" "}
                  <span className="font-semibold text-blue-600">
                    {selectedGroupIds.length}
                  </span>{" "}
                  group{selectedGroupIds.length !== 1 ? "s" : ""}
                </p>
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-sm font-semibold text-slate-900">Recent History</div>
                <div className="text-xs text-slate-500 mt-1">
                  Latest inventory changes for this item.
                </div>

                <div className="mt-3 space-y-3">
                  {historyRows.length === 0 ? (
                    <div className="text-xs text-slate-500">No item history yet.</div>
                  ) : (
                    historyRows.map((row) => {
                      const changedFields = Array.isArray(row?.changedFields) ? row.changedFields : [];
                      return (
                        <div key={row.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-900">
                                {String(row?.type || "updated").replace(/_/g, " ")}
                              </div>
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                {labelForUserId(row?.changedBy)}
                              </div>
                            </div>
                            <div className="text-[11px] text-slate-500 text-right">
                              {formatAuditDate(row?.changedAt)}
                            </div>
                          </div>
                          {changedFields.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {changedFields.slice(0, 6).map((field) => (
                                <span
                                  key={field}
                                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600"
                                >
                                  {field}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          {row?.priceChange ? (
                            <div className="mt-2 text-[11px] text-slate-600">
                              Price:{" "}
                              <span className="font-medium text-slate-900">
                                {row.priceChange.from == null ? "—" : `$${Number(row.priceChange.from).toFixed(2)}`}
                              </span>{" "}
                              to{" "}
                              <span className="font-medium text-slate-900">
                                {row.priceChange.to == null ? "—" : `$${Number(row.priceChange.to).toFixed(2)}`}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                  Audit Snapshot
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Last updated</span>
                    <span className="text-slate-900 font-medium text-right">
                      {formatAuditDate(currentUpdatedAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Updated by</span>
                    <span className="text-slate-900 font-medium text-right">
                      {labelForUserId(item?.updatedBy)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Created by</span>
                    <span className="text-slate-900 font-medium text-right">
                      {labelForUserId(item?.createdBy)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
        </form>

        {/* Footer - Fixed */}
        <div className="sticky bottom-0 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploadingImage}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canEdit || saving || uploadingImage}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploadingImage ? "Uploading..." : saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Product Parsing Panel Modal */}
      {showParser && item && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
          <div className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <ProductParsingPanel
              product={{
                id: item.id,
                name: formState.name,
                sku: formState.sku,
                packSize: formState.packSize,
                ...item
              }}
              onApplyFix={handleParserApply}
              onCancel={() => setShowParser(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(InventoryEditPanel);
